/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';

import computedFirst from 'cio/utils/computed-first';
import EnvironmentService from 'cio/services/environment';
import EnvironmentModel from 'cio/models/environment';
import { tracked } from '@glimmer/tracking';
import { IdentifierKey } from 'cio/utils/identifiers';
import { CIO_SUBSCRIPTION_PREF_KEY, CREATED_IN_CIO_AT } from 'cio/utils/attributes';

interface CioEnvironmentAttributesCollectionArgs {
  currentAttributes: Record<string, string>[];
  isSaving?: boolean;
  onAddAttribute: (attribute: Record<string, string>) => unknown;
  sentAttributes?: string[];
  rejectAttributes?: string[];
  isObject?: boolean;
}

export default class CioEnvironmentAttributesCollection extends Component<CioEnvironmentAttributesCollectionArgs> {
  @service environment!: EnvironmentService;

  @computedFirst('filteredAttributes', 100) visibleAttributes!: Record<string, string>[];

  @computed('environment.sentAttributes', 'args.sentAttributes')
  get environmentAttributes(): EnvironmentModel['sentAttributes'] {
    return this.args.sentAttributes ?? this.environment.sentAttributes;
  }

  @tracked searchString = '';

  @computed(
    'args.rejectAttributes',
    'environmentAttributes.[]',
    'currentAttributes.@each.{key,value}',
    'environment.current.allEnabledIdentifiers',
  )
  get availableAttributes() {
    let currentAttributesKeys = this.args.currentAttributes.mapBy('key');

    return this.environmentAttributes.reject((attributeKey) => {
      let attributesToReject: string[] = [];
      if (this.args.rejectAttributes != null) {
        attributesToReject = [
          ...currentAttributesKeys, // Remove defined attributes
          ...this.args.rejectAttributes,
          CIO_SUBSCRIPTION_PREF_KEY,
        ];
      } else {
        attributesToReject = [
          ...this.environment.current.allEnabledIdentifiers,
          ...currentAttributesKeys, // Remove defined attributes
          IdentifierKey.CioId,
          CREATED_IN_CIO_AT,
          CIO_SUBSCRIPTION_PREF_KEY,
        ];
      }
      return (
        attributesToReject.includes(attributeKey) ||
        // cio_subscription_preferences is a JSON attribute, filter out all
        // its keys:
        attributeKey.startsWith(`${CIO_SUBSCRIPTION_PREF_KEY}.`)
      );
    });
  }

  @computed('availableAttributes.[]', 'searchString')
  get filteredAttributes() {
    let searchString = this.searchString.trim();

    return this.availableAttributes.reject((attributeKey) => {
      return searchString ? attributeKey.indexOf(searchString) === -1 : void 0;
    });
  }

  @computed('filteredAttributes.length', 'availableAttributes.length')
  get allAvailableAttributesMatch() {
    return this.filteredAttributes.length === this.availableAttributes.length;
  }

  @computed('environment.moreAttributes')
  get searchPlaceholder() {
    if (this.environment.moreAttributes) {
      let maxAttributes = 2000;
      return `Search your first ${maxAttributes.toLocaleString()} attributes`;
    } else {
      return 'Search for attribute';
    }
  }

  @action
  addAttribute(attribute: Record<string, string>) {
    this.args.onAddAttribute(attribute);
  }
}
